import { isShapeKind } from './shape-limits.js';
/** Segment counts per detail level: fixed, never taken from the lesson. */
export const SEGMENTS = {
    full: { round: 24, rings: 12, tube: 10, tubular: 32, cap: 4, lathe: 24 },
    simple: { round: 12, rings: 6, tube: 6, tubular: 16, cap: 2, lathe: 12 },
};
/**
 * Triangles every shape in one lesson may add up to at full detail. Above it
 * the headset draws them all simpler, which is visibly faceted. A Quest 3
 * draws the skeleton lesson and the solar system well within this, twice over
 * (once per eye). Hidden objects count: they are built once, at the start.
 */
export const SHAPE_TRIANGLE_BUDGET = 150_000;
/** Below this a part covers less than a tenth of a millimetre squared (render/shapes.ts). */
const AREA_EPSILON = 1e-8;
/** Deeper than any lesson means anything by; stops a hostile answer before the stack does. */
const WALK_LIMIT = 16;
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const points = (value) => Array.isArray(value)
    ? value.filter((p) => Array.isArray(p) &&
        p.length === 2 &&
        typeof p[0] === 'number' &&
        typeof p[1] === 'number')
    : [];
const sized = (part, count) => Array.isArray(part['size']) &&
    part['size'].length === count &&
    part['size'].every((n) => typeof n === 'number');
/** An outline turned about y: two triangles per segment per step up, if it has any area. */
function latheTriangles(profile, s) {
    if (profile.length < 2)
        return 0;
    // A negative distance from the axis is drawn as 0.
    const turned = profile.map(([r, y]) => [Math.max(0, r), y]);
    let area = 0;
    for (let i = 1; i < turned.length; i++) {
        const [r1, y1] = turned[i - 1];
        const [r2, y2] = turned[i];
        area += Math.PI * (r1 + r2) * Math.hypot(r2 - r1, y2 - y1);
    }
    return area > AREA_EPSILON ? 2 * s.lathe * (turned.length - 1) : 0;
}
/**
 * An outline pushed out along z, as three.js 0.186 builds it without a bevel:
 * a repeated point, and a last one repeating the first, are dropped; every
 * corner left has two triangles of wall; each lid is the outline triangulated,
 * n corners in n − 2 triangles (earcut keeps a corner on a straight edge), and
 * an outline with no area has no lid. Exact for an outline that does not
 * cross itself, which is what the schema asks for.
 */
function extrudeTriangles(outline, depth) {
    const corners = [];
    for (const p of outline) {
        const last = corners[corners.length - 1];
        if (!last || last[0] !== p[0] || last[1] !== p[1])
            corners.push(p);
    }
    while (corners.length > 1) {
        const first = corners[0];
        const last = corners[corners.length - 1];
        if (first[0] !== last[0] || first[1] !== last[1])
            break;
        corners.pop();
    }
    const n = corners.length;
    if (n < 2)
        return 0;
    let perimeter = 0;
    let shoelace = 0;
    for (let i = 0; i < n; i++) {
        const [x1, y1] = corners[i];
        const [x2, y2] = corners[(i + 1) % n];
        perimeter += Math.hypot(x2 - x1, y2 - y1);
        shoelace += x1 * y2 - x2 * y1;
    }
    if (perimeter * depth + Math.abs(shoelace) <= AREA_EPSILON)
        return 0;
    const walls = 2 * n;
    if (shoelace === 0)
        return walls;
    return walls + 2 * (n - 2);
}
function partTriangles(part, s) {
    switch (part['type']) {
        case 'box':
            return sized(part, 3) ? 12 : 0;
        case 'plane':
            return sized(part, 2) ? 2 : 0;
        // No triangles at the poles: they would have no area.
        case 'sphere':
            return sized(part, 1) ? 2 * s.round * (s.rings - 1) : 0;
        // The side, and a cap at each end.
        case 'cylinder':
            return sized(part, 3) ? 4 * s.round : 0;
        // The side comes to a point, so one triangle a segment, and the base.
        case 'cone':
            return sized(part, 2) ? 2 * s.round : 0;
        case 'torus':
            return sized(part, 2) ? 2 * s.tube * s.tubular : 0;
        // Two rounded ends of `cap` rings each, and the straight middle.
        case 'capsule':
            return sized(part, 2) ? 2 * s.round * (2 * s.cap + 1) : 0;
        case 'lathe':
            return latheTriangles(points(part['points']), s);
        case 'extrude':
            return typeof part['depth'] === 'number'
                ? extrudeTriangles(points(part['outline']), part['depth'])
                : 0;
        default:
            return 0;
    }
}
function countParts(list, s, level) {
    if (!Array.isArray(list) || level > WALK_LIMIT)
        return 0;
    let total = 0;
    for (const part of list) {
        if (!isRecord(part))
            continue;
        total +=
            part['type'] === 'group' ? countParts(part['parts'], s, level + 1) : partTriangles(part, s);
    }
    return total;
}
/**
 * The triangles the headset draws for these parts, exactly. Nothing drawable
 * is the grey block's 12. Reads defensively: the generator counts answers
 * the schema may have refused.
 */
export function shapeTriangles(parts, detail) {
    return countParts(parts, SEGMENTS[detail], 1) || 12;
}
/** Every shape object in a lesson, added up; hidden ones too, since they are built at the start. */
export function lessonTriangles(lesson, detail = 'full') {
    if (!isRecord(lesson) || !isRecord(lesson['objects']))
        return 0;
    let total = 0;
    for (const object of Object.values(lesson['objects'])) {
        if (!isRecord(object) || typeof object['kind'] !== 'string' || !isShapeKind(object['kind'])) {
            continue;
        }
        // A shape without parts is the validator's to refuse; the headset builds nothing for it.
        if (!Array.isArray(object['parts']))
            continue;
        total += shapeTriangles(object['parts'], detail);
    }
    return total;
}
//# sourceMappingURL=shape-cost.js.map